import type {
  HubAlgorithmDefinition,
  HubExampleDefinition,
  HubJobKind,
  HubMetricDefinition,
} from '@robovision/hub'
import {
  groupBy,
  mapToObject,
} from '@robovision/rvai-utils'
import { Exceptions } from '~domain/Exceptions'
import { DeploymentDefinition } from './jobDefinitions/DeploymentDefinition'
import { JobDefinition } from './jobDefinitions/JobDefinition'


export class AlgorithmDefinition {
  private readonly deploymentDefinitions: DeploymentDefinition[]
  private readonly jobDefinitions: Partial<Record<HubJobKind, JobDefinition[]>>
  private readonly projectMetadataKeys: string[]
  private readonly rawDefinition: HubAlgorithmDefinition

  private constructor (definition: HubAlgorithmDefinition) {
    this.rawDefinition = definition

    this.jobDefinitions = mapToObject(
      Object.fromEntries(groupBy(definition.jobs ?? [], 'kind')),
      arr => arr.map(j => new JobDefinition(j, definition)),
    )

    this.deploymentDefinitions = (definition.deployments ?? [])
      .map(d => new DeploymentDefinition(d, definition))

    this.projectMetadataKeys = definition.projectMetadataKeys ?? []

    Object.freeze(this)
  }

  public get allJobDefinitions (): JobDefinition[] {
    return Object.values(this.jobDefinitions).flat()
  }

  public get datapoolDefinitions () {
    return this.rawDefinition.datapoolDefinitions
  }

  public get frontEndConfig () {
    return this.rawDefinition.frontendConfig
  }

  public get modelDefinitions () {
    return this.rawDefinition.modelDefinitions
  }

  public get exampleDefinitions () {
    return this.rawDefinition.exampleDefinitions
  }

  public get id () {
    return this.rawDefinition.id
  }

  public get type () {
    return this.rawDefinition.type
  }

  public get jobKinds (): HubJobKind[] {
    return Object.entries(this.jobDefinitions)
      .filter(([, jobs]) => !!jobs.length)
      .map(([kind]) => kind as HubJobKind)
  }

  public get key () {
    return `${this.rawDefinition.id}__${this.rawDefinition.version}`
  }

  public get metadataKeys (): string[] {
    return this.projectMetadataKeys
  }

  public get metricDefinitions (): HubMetricDefinition[] {
    return this.rawDefinition.metricDefinitions ?? []
  }

  public get name (): string {
    return this.rawDefinition.name
  }

  public get version () {
    return this.rawDefinition.version
  }

  public supportsJobKind (jobKind: HubJobKind): boolean {
    return this.jobKinds.includes(jobKind)
  }

  public getJobDefinitionById (definitionId: string): JobDefinition | undefined
  public getJobDefinitionById (definitionId: string, throwIfMissing: true): JobDefinition
  public getJobDefinitionById (definitionId: string, throwIfMissing: boolean = false): JobDefinition | undefined {
    for (const jobs of Object.values(this.jobDefinitions)) {
      for (const job of jobs) {
        if (job.id === definitionId) return job
      }
    }

    if (throwIfMissing) {
      throw new Exceptions.JobDefinitionNotFound(
        `Algorithm ${this.id}:${this.version} does not contain job definition ${definitionId}`,
      )
    }
  }

  public getJobDefinitionsByKind (kind: HubJobKind): JobDefinition[] {
    return this.jobDefinitions[kind] ?? []
  }

  public getJobDefinitionsByKinds (kinds: HubJobKind[]): JobDefinition[] {
    const filteredDefinitions: JobDefinition[] = []
    for (const key in this.jobDefinitions) {
      if (kinds.includes(key as HubJobKind)) {
        filteredDefinitions.push(...this.jobDefinitions[key as HubJobKind] ?? [])
      }
    }

    return filteredDefinitions
  }

  public getCustomChartsForCenter (centerType: 'label' | 'test') {
    switch (centerType) {
      case 'label':
        return this.frontEndConfig?.labelCenter?.charts
      case 'test':
        return this.frontEndConfig?.testCenter?.charts
      default:
        return undefined
    }
  }

  public hasParameterField (key: string): boolean {
    const parameterDefinitions = this.rawDefinition.parameterDefinitions ?? []

    return parameterDefinitions.some(def => def.schema?.fields.some(f => f.key === key))
  }

  public static fromResponse (definition: HubAlgorithmDefinition) {
    return new AlgorithmDefinition(definition)
  }

  /**
   * @deprecated
   * Backward compatible prop which supports a single job per kind
   */
  public get jobs (): Partial<Record<HubJobKind, JobDefinition>> {
    return mapToObject(
      this.jobDefinitions as Record<HubJobKind, JobDefinition[]>,
      j => j?.[0],
    )
  }

  public getDeploymentDefinitionById (definitionId: string): DeploymentDefinition | undefined
  public getDeploymentDefinitionById (definitionId: string, throwIfMissing: true): DeploymentDefinition
  public getDeploymentDefinitionById (
    definitionId: string,
    throwIfMissing: boolean = false,
  ): DeploymentDefinition | undefined {
    const definition = this.deploymentDefinitions.find(definition => definition.id === definitionId)
    if (definition) return definition

    if (throwIfMissing) {
      throw new Error(
        `Algorithm ${this.id}:${this.version} does not contain job definition ${definitionId}`,
      )
    }
  }

  public getExampleDefinitionById (
    exampleDefinitionId: string,
  ): HubExampleDefinition | undefined {
    return this.exampleDefinitions?.find(definition =>
      definition.id === exampleDefinitionId)
  }

  /**
   * @deprecated
   * Backward compatible prop which supports a single deployment
   */
  public get deployment (): DeploymentDefinition | undefined {
    return this.deploymentDefinitions[0]
  }

  public get deployments (): DeploymentDefinition[] {
    return this.deploymentDefinitions
  }
}
